import type { CreditEnvelope,Invoice,PaymentIntent,PaymentIntentResult } from '@algoboo/schema/api'
import { AddressLink,Button,Card,CreditMeter,EmptyState,Eyebrow,Field,Input,Money,Pill,Skeleton,UiLink,cx,formatCredits,formatUsd,fullTime,timeAgo,type PillTone } from '@algoboo/ui'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute,useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { PageHeader } from '../components/Shell'
import { TxButton } from '../components/TxButton'
import { SignInPrompt,useSession } from '../components/session'
import { ENVELOPE_KIND } from '../components/status'
import { creditsQuery, errorCopy, invalidate, isApiError, myInvoicesQuery, paymentsQuery, qk, request, useApiMutation, useCredits, useMyInvoices, usePayment, usePayments } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { prefetchSignedIn } from '../lib/route-data'

export const Route = createFileRoute('/portfolio/billing')({
  validateSearch: z.object({ intent: z.string().optional(), invoice: z.string().optional() }),
  loader: ({ context: { queryClient: qc } }) =>
    prefetchSignedIn(qc, () => [qc.ensureQueryData(myInvoicesQuery()), qc.ensureInfiniteQueryData(paymentsQuery()), qc.ensureQueryData(creditsQuery())]),
  component: BillingPage,
})

const TOPUP_PRESETS = ['5', '20', '50', '100'] as const
const MAX_TOPUP_USD = 10_000

const STATUS: Record<PaymentIntent['status'], { tone: PillTone; label: string }> = {
  pending: { tone: 'warn', label: 'Pending' },
  confirmed: { tone: 'good', label: 'Confirmed' },
  expired: { tone: 'neutral', label: 'Expired' },
}

const KIND_LABEL: Record<PaymentIntent['kind'], string> = { plan: 'Plan', topup: 'Credit top-up', subscription: 'Agent subscription', invoice: 'Invoice', agent_creation: 'Agent creation' }

function BillingPage() {
  usePageMeta('Billing')
  const { me, loading } = useSession()
  if (loading) return <Skeleton className="h-64 rounded-xl" />
  if (!me) return <SignInPrompt title="Billing" body="Compute credits and payments. Sign in with your wallet to manage them." />
  return <Billing />
}

/** A valid top-up: positive whole cents up to USD 10 000. */
export function parseTopupUsd(v: string): string | null {
  if (!/^\d{1,5}(\.\d{1,2})?$/.test(v.trim())) return null
  const n = Number(v)
  if (!(n > 0) || n > MAX_TOPUP_USD) return null
  return n.toFixed(2)
}

function Billing() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const credits = useCredits()
  const parked = search.intent ? qc.getQueryData<PaymentIntentResult>(qk.pendingPayment(search.intent)) : undefined
  const [active, setActive] = useState<PaymentIntentResult | null>(parked ?? null)
  const [err, setErr] = useState<string | null>(null)
  const onResult = (r: PaymentIntentResult) => {
    setErr(null)
    setActive(r)
    void Promise.all([...invalidate.payments(), qk.myInvoices().slice(0, 2)].map((queryKey) => qc.invalidateQueries({ queryKey })))
  }
  const onError = (e: unknown) => setErr(isApiError(e) ? `${errorCopy(e.code).title}: ${e.message}` : e instanceof Error ? e.message : String(e))

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="Billing"
        lede="Compute credits are paid in USDG on Robinhood Chain, straight from your wallet to the platform treasury. 1 credit pays for USD 0.01 of model cost."
        actions={
          <UiLink href="/portfolio" className="text-[13px] font-medium text-accent hover:underline">
            Back to portfolio
          </UiLink>
        }
      />
      {active && (
        <PaymentPanel
          result={active}
          onClose={() => {
            setActive(null)
            if (search.intent) void navigate({ to: '/portfolio/billing', search: {} })
          }}
        />
      )}
      {!active && search.intent && <ResumeIntent id={search.intent} />}
      {err && (
        <p role="alert" className="mb-4 rounded-md border border-danger px-3 py-2 text-[13px] text-danger">
          {err}
        </p>
      )}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <Card className="p-5">
            <h2 className="font-display text-[18px] font-semibold">Credits</h2>
            {credits.data ? (
              <>
                <CreditMeter className="mt-3" availableMicro={credits.data.availableMicro} heldMicro={credits.data.heldMicro} settledMicro={credits.data.settledMicro} />
                <Envelopes envelopes={credits.data.envelopes} />
              </>
            ) : (
              <Skeleton className="mt-3 h-24" />
            )}
          </Card>
          <Invoices highlight={search.invoice} onResult={onResult} onError={onError} />
          <History />
        </div>
        <aside>
          <TopupCard onResult={onResult} onError={onError} />
        </aside>
      </div>
    </>
  )
}

function Envelopes({ envelopes }: { envelopes: CreditEnvelope[] }) {
  const open = envelopes.filter((e) => e.status === 'open')
  if (open.length === 0) return <p className="mt-3 text-[13px] text-ink-3">No open credit balance.</p>
  return (
    <ul className="mt-4 divide-y divide-line text-[13px]" aria-label="Credit balances">
      {open.map((e) => (
        <li key={e.id} className="flex items-center gap-3 py-2">
          <Pill tone={e.kind === 'prepaid' ? 'info' : 'neutral'}>{ENVELOPE_KIND[e.kind]}</Pill>
          <span className="num">
            {formatCredits(e.availableMicro)} / {formatCredits(e.limitMicro)} credits
          </span>
          <span className="ml-auto text-ink-3" title={fullTime(e.periodEnd)}>
            {e.kind === 'prepaid' ? 'no expiry' : `until ${e.periodEnd.slice(0, 10)}`}
          </span>
        </li>
      ))}
    </ul>
  )
}

function TopupCard({ onResult, onError }: { onResult: (r: PaymentIntentResult) => void; onError: (e: unknown) => void }) {
  const [amount, setAmount] = useState('20')
  const topup = useApiMutation('topupCredits', { onSuccess: onResult })
  const valid = parseTopupUsd(amount)
  return (
    <Card className="p-5">
      <Eyebrow>Top up credits</Eyebrow>
      <p className="mt-1 text-[12.5px] text-ink-3">Prepaid credits never expire. USD 1 buys 100 credits.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {TOPUP_PRESETS.map((v) => (
          <Button key={v} size="sm" variant={amount === v ? 'secondary' : 'ghost'} onClick={() => setAmount(v)}>
            {formatUsd(v, { precision: 0 })}
          </Button>
        ))}
      </div>
      <form
        className="mt-3 space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (valid) topup.mutate({ body: { amountUsd: valid } }, { onError })
        }}
      >
        <Field htmlFor="topup-amount" label="Amount (USD)" error={amount && !valid ? `Whole cents, up to ${formatUsd(String(MAX_TOPUP_USD), { precision: 0 })}` : undefined}>
          <Input id="topup-amount" inputMode="decimal" prefix="$" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        {valid && <p className="text-[12.5px] text-ink-3">{formatCredits(String(Math.round(Number(valid) * 100) * 1_000_000))} credits</p>}
        <Button type="submit" variant="primary" icon="bolt" disabled={!valid} loading={topup.isPending}>
          Continue to payment
        </Button>
      </form>
    </Card>
  )
}

/** Walks the wallet through `approve` (when needed) then `pay`, and waits for the indexer to confirm. */
export function PaymentPanel({ result, onClose }: { result: PaymentIntentResult; onClose: () => void }) {
  const { intent, txs } = result
  const approve = txs.length > 1 ? txs[0]! : null
  const pay = txs[txs.length - 1] ?? null
  const [approved, setApproved] = useState(approve === null)
  const [sent, setSent] = useState(false)
  const qc = useQueryClient()
  // Poll only once the pay tx is out (the indexer cannot have seen it before).
  const live = usePayment(intent.id, intent.status === 'pending' && sent)
  const status = live.data?.status ?? intent.status
  const tokenAmount = `${formatUsd(intent.amountUsd, { precision: 2 }).replace(/^\$/, '')} ${intent.tokenSymbol}`
  const what =
    intent.kind === 'plan' ? `${intent.planId} plan` : intent.kind === 'subscription' ? `@${intent.agent?.handle ?? 'agent'} (${intent.tier})` : intent.kind === 'invoice' ? `an invoice${intent.agent ? ` of @${intent.agent.handle}` : ''}` : 'credit top-up'
  return (
    <Card className="mb-6 border-brand p-5" aria-label="Payment" data-testid="payment-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-[18px] font-semibold">
          Pay <Money value={intent.amountUsd} /> for {what}
        </h2>
        <Pill tone={STATUS[status].tone}>{STATUS[status].label}</Pill>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={onClose}>
          {status === 'confirmed' ? 'Close' : 'Cancel'}
        </Button>
      </div>
      {status === 'confirmed' ? (
        <p className="mt-3 text-[14px] text-gain" data-testid="payment-confirmed">
          Payment confirmed{intent.kind === 'topup' ? ': your credits are available.' : intent.kind === 'plan' ? ': recorded in payment history.' : intent.kind === 'invoice' ? ': the invoice is paid.' : ': recorded in payment history.'}
          {(live.data?.txHash ?? intent.txHash) && <AddressLink kind="tx" value={(live.data?.txHash ?? intent.txHash)!} className="ml-2" copy={false} />}
        </p>
      ) : (intent.kind === 'plan' || intent.kind === 'subscription') ? (
        <p className="mt-3 text-[13px] text-ink-3">This offer has ended. An already sent payment remains in your history.</p>
      ) : status === 'expired' ? (
        <p className="mt-3 text-[13px] text-ink-3">This payment request expired. Start again; nothing was charged unless you already sent the payment, in which case it is still credited.</p>
      ) : pay ? (
        <ol className="mt-4 space-y-3 text-[14px]">
          {approve && (
            <li className="flex flex-wrap items-center gap-3">
              <span className="w-5 font-mono text-ink-3">1.</span>
              <span className="min-w-40">Allow the treasury to take {tokenAmount}</span>
              <TxButton tx={approve} label={`Approve ${intent.tokenSymbol}`} variant="soft" onDone={() => setApproved(true)} reason="Sign in to pay." />
            </li>
          )}
          <li className="flex flex-wrap items-center gap-3">
            <span className="w-5 font-mono text-ink-3">{approve ? '2.' : '1.'}</span>
            <span className="min-w-40">Send {tokenAmount}</span>
            <TxButton
              tx={pay}
              label={`Pay ${tokenAmount}`}
              disabled={!approved}
              invalidate={[qk.payment(intent.id), ...invalidate.payments()]}
              isIndexed={async () => (await request('getMyPayment', { params: { id: intent.id } })).status === 'confirmed'}
              onDone={() => {
                setSent(true)
                void Promise.all(invalidate.payments().map((queryKey) => qc.invalidateQueries({ queryKey })))
              }}
              reason="Sign in to pay."
            />
          </li>
          <li className="pl-8 text-[12px] text-ink-3">
            Treasury <AddressLink value={intent.treasury} /> · ref <span className="font-mono">{intent.ref.slice(0, 10)}…</span> · request expires {timeAgo(intent.expiresAt)}
          </li>
        </ol>
      ) : (
        <p className="mt-3 text-[13px] text-ink-3">Waiting for confirmation…</p>
      )}
    </Card>
  )
}

function ResumeIntent({ id }: { id: string }) {
  const p = usePayment(id)
  if (!p.data) return null
  return (
    <Card className="mb-6 p-5">
      <p className="text-[14px]">
        {KIND_LABEL[p.data.kind]} <Money value={p.data.amountUsd} /> <Pill tone={STATUS[p.data.status].tone}>{STATUS[p.data.status].label}</Pill>
      </p>
      {p.data.status === 'pending' && <p className="mt-2 text-[13px] text-ink-3">Waiting for the payment on chain. If you did not send it, start again below.</p>}
    </Card>
  )
}

function History() {
  const h = usePayments()
  const items = h.data?.pages.flatMap((p) => p.items) ?? []
  return (
    <Card className="overflow-hidden">
      <h2 className="border-b border-line px-5 py-3 font-display text-[18px] font-semibold">Payments</h2>
      {h.isLoading ? (
        <Skeleton className="m-5 h-16" />
      ) : items.length === 0 ? (
        <EmptyState compact title="No payments yet" body="Launch payments, compute top-ups and past payments show up here." />
      ) : (
        <ul className="divide-y divide-line text-[13px]" aria-label="Payment history">
          {items.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5">
              <span className="min-w-36 font-medium">
                {KIND_LABEL[p.kind]}
                {p.planId && <span className="text-ink-3"> · {p.planId}</span>}
                {p.agent && <span className="text-ink-3"> · @{p.agent.handle}</span>}
              </span>
              <Money value={p.amountUsd} />
              <Pill tone={STATUS[p.status].tone}>{STATUS[p.status].label}</Pill>
              {p.txHash && <AddressLink kind="tx" value={p.txHash} copy={false} />}
              <span className="ml-auto text-ink-3" title={fullTime(p.createdAt)}>
                {timeAgo(p.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {h.hasNextPage && (
        <div className="border-t border-line px-5 py-2">
          <Button size="sm" variant="ghost" loading={h.isFetchingNextPage} onClick={() => void h.fetchNextPage()}>
            Older payments
          </Button>
        </div>
      )}
    </Card>
  )
}

const INVOICE_KIND: Record<Invoice['kind'], string> = { performance_fee: 'Performance fee', plan_renewal: 'Plan renewal', subscription_renewal: 'Subscription renewal' }
const INVOICE_STATUS: Record<Invoice['status'], { tone: PillTone; label: string }> = {
  open: { tone: 'warn', label: 'Open' },
  overdue: { tone: 'bad', label: 'Overdue' },
  paid: { tone: 'good', label: 'Paid' },
  void: { tone: 'neutral', label: 'Void' },
}

/** Performance fees and renewals owed; open and overdue ones first, each with a pay button. */
function Invoices({ highlight, onResult, onError }: { highlight: string | undefined; onResult: (r: PaymentIntentResult) => void; onError: (e: unknown) => void }) {
  const q = useMyInvoices()
  const pay = useApiMutation('payMyInvoice', { onSuccess: onResult })
  const items = (q.data ?? []).slice().sort((a, b) => Number(b.status === 'overdue' || b.status === 'open') - Number(a.status === 'overdue' || a.status === 'open'))
  return (
    <Card className="overflow-hidden" data-testid="invoices">
      <h2 className="border-b border-line px-5 py-3 font-display text-[18px] font-semibold">Invoices</h2>
      {q.isLoading ? (
        <Skeleton className="m-5 h-16" />
      ) : items.length === 0 ? (
        <p className="px-5 py-4 text-[13px] text-ink-3">No invoices. Subscription renewals and performance fees show up here when they are due.</p>
      ) : (
        <ul className="divide-y divide-line text-[13px]" aria-label="Invoices">
          {items.map((i) => (
            <li key={i.id} className={cx('flex flex-wrap items-center gap-3 px-5 py-2.5', highlight === i.id && 'bg-brand-soft')} data-testid="invoice-row" data-invoice-id={i.id} data-status={i.status}>
              <span className="min-w-40 font-medium">
                {INVOICE_KIND[i.kind]}
                {i.agent && <span className="text-ink-3"> · @{i.agent.handle}</span>}
              </span>
              <Money value={i.amountUsd} />
              <Pill tone={INVOICE_STATUS[i.status].tone}>{INVOICE_STATUS[i.status].label}</Pill>
              <span className="text-ink-3" title={fullTime(i.dueAt)}>
                {i.status === 'paid' && i.paidAt ? `paid ${timeAgo(i.paidAt)}` : `due ${i.dueAt.slice(0, 10)}`}
              </span>
              {(i.status === 'open' || i.status === 'overdue') && (
                <Button size="sm" variant={i.status === 'overdue' ? 'primary' : 'soft'} className="ml-auto" loading={pay.isPending && pay.variables?.params.id === i.id} onClick={() => pay.mutate({ params: { id: i.id } }, { onError })}>
                  Pay
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
